import React, { useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import { useIntl } from '@atlassian/jira-intl';
import { useObjectStoreState } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/store/index.tsx';
import { CollapsibleGroup } from '@atlassian/jira-servicedesk-insight-shared-ui/src/ui/collapsible-group/index.tsx';
import { InsightObjectViewPanelSkeleton } from '@atlassian/jira-skeletons/src/ui/insight-object/InsightObjectViewSkeleton.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { preloadImg } from '../../common/utils.tsx';
import messages from './messages.tsx';
import { AvatarError } from './avatar-error/index.tsx';

export const AvatarPanel = () => {
	const [{ objectDetails }] = useObjectStoreState();
	const [imageLoading, setImageLoading] = useState<boolean>(false);
	const [imageFetchError, setImageFetchError] = useState<boolean>(false);
	const avatarUrl288 = objectDetails?.avatar.url288;
	const avatarUUID = objectDetails?.avatar.avatarUUID;
	// adding avatarUUID as query params to disable browser caching for when avatar gets updated
	const avatarUrl = `${avatarUrl288}${avatarUUID ? `?avatarUUID=${avatarUUID}` : ''}`;

	useEffect(() => {
		if (avatarUrl) {
			setImageFetchError(false);
			setImageLoading(true);
			preloadImg(avatarUrl)
				.catch((error) => {
					setImageFetchError(true);
					fireErrorAnalytics({
						error: error instanceof Error ? error : undefined,
						meta: {
							id: 'preloadObjectAvatarImg',
							packageName: 'jiraServicedeskCmdbAvatarPanel',
							teamName: 'falcons',
						},
					});
				})
				.finally(() => {
					setImageLoading(false);
				});
		}
	}, [avatarUrl]);

	const { formatMessage } = useIntl();

	if (!objectDetails?.hasAvatar) {
		return null;
	}

	let avatarContent;
	if (imageLoading) {
		avatarContent = (
			<InsightObjectViewPanelSkeleton data-testId="servicedesk-insight-object-view.ui.avatar-panel.avatar-loading" />
		);
	} else if (imageFetchError) {
		avatarContent = <AvatarError />;
	} else {
		avatarContent = (
			<AvatarWrapper>
				<Image
					data-testId="servicedesk-insight-object-view.ui.avatar-panel.avatar-image"
					src={avatarUrl}
					alt={formatMessage(messages.avatarImageAlt)}
				/>
			</AvatarWrapper>
		);
	}

	return (
		<CollapsibleGroup
			title={formatMessage(messages.panelTitle)}
			testId="servicedesk-insight-object-view.ui.avatar-panel"
			headingLevel="h2"
		>
			{avatarContent}
		</CollapsibleGroup>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarWrapper = styled.div({
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Image = styled.img({
	width: '100%',
	flexBasis: 'fit-content',
});
